// eslint-disable-next-line simple-import-sort/imports
import type { FC } from "react";

import dompurify from "isomorphic-dompurify";

import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import { useConfig } from "@jti/app-context/useConfig";

import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";

import { useUser } from "@ui/hooks/useUser";

import { FlavorFinderSkipButton, FlavorFinderStartQuizButton } from "../Buttons/FlavorFinderButtons";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";

import "./FlavorFinderIntro.scss";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";

const { sanitize } = dompurify;

export const FlavorFinderIntro: FC = () => {
  const { currentBreakpoint } = useCurrentBreakpoint();
  const { startQuiz, flavorFinderData } = useFlavorFinderContext();
  const { "base-url": baseUrl } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const { isUserLoggedIn } = useUser();
  const {
    titleLoggedIn,
    titleNonLoggedIn,
    subtitle,
    subtitlePostscript,
    indicatorOverrideLabel,
    introBackgroundImage,
    smallIntroBackgroundImage,
    mediumIntroBackgroundImage,
    introBackgroundVideo,
    mediumIntroBackgroundVideo,
    smallIntroBackgroundVideo,
    ctaLabelStartLoggedIn,
    ctaLabelStartNonLoggedIn,
    ctaLabelLoginToStart,
  } = flavorFinderData.intro;

  const getVideoAndBackgroundImage = () => {
    switch (currentBreakpoint) {
      case "small":
        return {
          backgroundImage: smallIntroBackgroundImage,
          video: smallIntroBackgroundVideo,
        };
      case "medium":
        return {
          backgroundImage: mediumIntroBackgroundImage,
          video: mediumIntroBackgroundVideo,
        };
      case "large":
      default:
        return {
          backgroundImage: introBackgroundImage,
          video: introBackgroundVideo,
        };
    }
  };

  const goToLoginPage = () => {
    window.location.href = resolveAuthHref({
      route: AUTH_ROUTE_PATHS.login,
      search: { returnUrl: baseUrl },
    });
  };

  const { backgroundImage, video } = getVideoAndBackgroundImage();
  const containerStyle = video ? {} : { backgroundImage: `url('${backgroundImage}')` };

  return (
    <div style={containerStyle} className="flavorfinder__intro" data-testid="flavor-finder-intro">
      <FlavorFinderHeader title={isUserLoggedIn ? titleLoggedIn : titleNonLoggedIn} infoText={indicatorOverrideLabel} />
      <div className="flavorfinder__intro-content">
        <div className="flavorfinder__intro-subtitle" dangerouslySetInnerHTML={{ __html: sanitize(subtitle) }}></div>
        {Boolean(subtitlePostscript) && (
          <div
            className="flavorfinder__intro-subtitlePostscript"
            dangerouslySetInnerHTML={{ __html: sanitize(subtitlePostscript ?? "") }}
          ></div>
        )}
        {video && (
          <video
            className="flavorfinder__intro-video"
            src={video}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
          />
        )}
      </div>
      <div className="flavorfinder__footer">
        {!isUserLoggedIn && <FlavorFinderStartQuizButton onClick={goToLoginPage} label={ctaLabelLoginToStart} />}
        {!isUserLoggedIn && <FlavorFinderSkipButton label={ctaLabelStartNonLoggedIn} onClick={startQuiz} />}
        {isUserLoggedIn && <FlavorFinderStartQuizButton label={ctaLabelStartLoggedIn} onClick={startQuiz} />}
      </div>
    </div>
  );
};
